import { TextField, Button, Typography, Stack, Box } from '@mui/material';
import { styledTextField } from '@old/styles/generic-styles';
import { useNavigate, useSearchParams } from 'react-router';
import { PATH } from '@old/data/core/paths';
import { useGlobalStore } from '@old/global-store/use-store';
import { useState, useEffect, useRef } from 'react';
import { useService } from '@old/hooks/use-service';
import { ToastAction } from '@old/ui/toast/toast-action';
import CircularProgress from '@mui/material/CircularProgress';
import i18n from '../../../libs/language';
import { verifyCredentialService } from '@old/services/domain/auth/verify-auth-credential';
import { recoverCredentialService } from '@old/services/domain/auth/recover-auth-credential';

type VerifyCrendentialBody = Parameters<typeof verifyCredentialService>[0]['body'];

export function VerifyRegisterForm() {
  const { email: storeEmail, setLanguage } = useGlobalStore();
  const [searchParams] = useSearchParams();

  const paramEmail = searchParams.get('email') ?? '';
  const paramCode = searchParams.get('code');
  const paramLang = searchParams.get('lang');

  // Aplica el idioma desde el URL param (viene en el link del email de verificación)
  useEffect(() => {
    const lang = paramLang === 'pt' ? 'pt' : paramLang === 'es' ? 'es' : null;
    if (lang) {
      i18n.locale = lang;
      setLanguage(lang);
    }
  }, [paramLang]);

  const resolvedEmail = paramEmail || storeEmail || '';

  const [formVerifyRegister, setFormVerifyRegister] = useState<VerifyCrendentialBody>({
    username: resolvedEmail,
    verification_code: paramCode !== null ? Number(paramCode) : null,
  });

  const navigate = useNavigate();
  const autoSubmitFired = useRef(false);

  const {
    trigger: sendVerify,
    isError: isErrorVerify,
    isLoading,
  } = useService(verifyCredentialService, {
    onSuccess: () =>
      navigate(`${PATH.COMPLETE_REGISTER}?email=${encodeURIComponent(formVerifyRegister.username ?? '')}`, {
        replace: true,
      }),
  });

  // Auto-submit cuando llegan email + code por query params
  useEffect(() => {
    if (paramEmail && paramCode && !autoSubmitFired.current) {
      autoSubmitFired.current = true;
      const timer = setTimeout(() => {
        sendVerify({
          body: {
            username: paramEmail,
            verification_code: Number(paramCode),
          },
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const { trigger: resendCode, isSuccess, isError } = useService(recoverCredentialService);

  const handleVerify = (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (formVerifyRegister.verification_code !== undefined) {
      sendVerify({ body: formVerifyRegister });
    }
  };

  const resendCodeData = {
    email: formVerifyRegister.username,
  };

  const handleResendCode = () => {
    resendCode({ body: resendCodeData });
  };
  return (
    <Stack
      direction="column"
      display="flex"
      gap={2}
      justifyContent="center"
      alignItems="center"
      width={{ md: '50%' }}
    >
      <Box textAlign={{ xs: 'center', md: 'start' }} m={0}>
        <Typography
          fontSize={{ xs: '20px', md: '30px' }}
          mr={{ xs: 5, md: 0 }}
          ml={{ xs: 3, md: 0 }}
          fontWeight="bold"
          textAlign="center"
          sx={{
            textWrap: 'balance',
          }}
        >
          {i18n.t('app.public_pages.verify_register_title')}
        </Typography>
        <Typography fontSize={12} color="textSecondary" mt={1} textAlign="center">
          {i18n.t('app.public_pages.verify_register_description')}
        </Typography>
      </Box>
      <Stack spacing={2}>
        <TextField
          required
          id="verification_code"
          label={i18n.t('app.public_pages.verify_register_code')}
          variant="outlined"
          placeholder={i18n.t('app.public_pages.verification_code_placeholder')}
          size="small"
          sx={styledTextField}
          onChange={(e) => {
            setFormVerifyRegister((formVerifyRegister) => ({
              ...formVerifyRegister,
              verification_code: +e.target.value,
            }));
          }}
        />
        <Button
          variant="contained"
          sx={{ width: '400px', color: 'white' }}
          disableElevation
          onClick={handleVerify}
        >
          {isLoading ? (
            <CircularProgress sx={{ marginLeft: '10px', color: 'white' }} size={24} />
          ) : (
            'Enviar Codigo'
          )}
        </Button>
      </Stack>

      <Stack>
        <Typography fontSize={12} fontWeight={600} color="textSecondary">
          {i18n.t('app.public_pages.no_received_code')} ←
        </Typography>
        <Typography fontSize={12} color="textSecondary">
          → {i18n.t('app.public_pages.no_received_code_decription')}
        </Typography>
      </Stack>
      <Stack direction="row" mt={2}>
        <TextField
          id="email"
          label={i18n.t('app.public_pages.email')}
          variant="outlined"
          size="small"
          disabled={resolvedEmail !== ''}
          defaultValue={resolvedEmail}
          style={{
            width: '200px',
            marginRight: '10px',
          }}
          sx={styledTextField}
          onChange={(e) => {
            setFormVerifyRegister((prev) => ({
              ...prev,
              username: resolvedEmail !== '' ? resolvedEmail : e.target.value,
            }));
          }}
        />
        <Button
          variant="outlined"
          sx={{ width: '200px' }}
          size="medium"
          disableElevation
          onClick={handleResendCode}
        >
          Reenviar Codigo
        </Button>
      </Stack>
      {isSuccess && <ToastAction message="Se ha enviado el codigo de verificación" severity="success" />}
      {isError && <ToastAction message="Reenvia el codigo a un email valido" severity="error" />}
      {isErrorVerify && (
        <ToastAction
          message="Error al verificar el codigo de verificación, Verifica que sea el correcto"
          severity="error"
        />
      )}
    </Stack>
  );
}
